import React, { ButtonHTMLAttributes } from "react";
import styled from "styled-components";
import { GenericSvg, SVGName } from "../../../assets/svgs/GenericSVG";

import { SkeletonPlaceholder } from "../loading/SkeletonPlaceholder";
import { CustomButton } from "./CustomButton";

type Props = ButtonHTMLAttributes<HTMLButtonElement> & {
  onClick?: (event: React.MouseEvent) => void;
  iconName: SVGName;
  primaryColor?: string;
  secondaryColor?: string;
  size?: number;
  className?: string;
  disabled?: boolean;
  noPadding?: boolean;
  bgCircle?: {
    color: string;
    size?: number;
    borderRadius?: number;
  };
  loading?: boolean;
};

export const IconButton = React.forwardRef(
  (props: Props, ref: React.Ref<HTMLButtonElement>) => {
    const {
      onClick,
      iconName,
      primaryColor,
      secondaryColor,
      size,
      className,
      disabled,
      noPadding,
      bgCircle,
      ...restProps
    } = props;
    return (
      <StyledCustomButton
        onClick={onClick}
        className={className}
        disabled={disabled}
        $noPadding={noPadding}
        ref={ref}
        {...restProps}
      >
        {props.loading ? (
          <IconPlaceholder />
        ) : (
          <>
            <GenericSvg
              name={iconName}
              primaryColor={primaryColor}
              secondaryColor={secondaryColor}
              className="icon"
              size={size ?? 24}
              style={{ zIndex: 1 }}
            />
            <BackgroundCircle
              $color={bgCircle ? bgCircle.color : undefined}
              $size={bgCircle ? bgCircle.size : undefined}
              $borderRadius={bgCircle ? bgCircle.borderRadius : undefined}
            />
          </>
        )}
      </StyledCustomButton>
    );
  }
);

IconButton.displayName = "IconButton";

const IconPlaceholder = styled(SkeletonPlaceholder)`
  width: 24px;
  height: 24px;
`;

const StyledCustomButton = styled(CustomButton)<{ $noPadding?: boolean }>`
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  width: ${(props) => (props.$noPadding ? "auto" : "48px")};
  height: ${(props) => (props.$noPadding ? "auto" : "48px")};
  cursor: pointer;
`;
const BackgroundCircle = styled.div<{
  $color?: string;
  $size?: number;
  $borderRadius?: number;
}>`
  position: absolute;
  width: 36px;
  height: 36px;
  background-color: ${(props) => props.$color || "transparent"};
  width: ${(props) => (props.$size ? `${props.$size}px` : "36px")};
  height: ${(props) => (props.$size ? `${props.$size}px` : "36px")};
  border-radius: ${(props) =>
    props.$borderRadius ? `${props.$borderRadius}px` : "50%"};
  z-index: 0;
`;
